/**
 * PipelineProgress Component
 *
 * Visual representation of pipeline stages with status indicators.
 *
 * Date: 2026-03-11
 */

import { CheckCircle2, Circle, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PipelineTargetStatus } from '@/services/pipelineTrackerService';
import { getStageStatus } from '@/services/pipelineTrackerService';

interface PipelineProgressProps {
  status: PipelineTargetStatus;
  compact?: boolean;
}

export function PipelineProgress({ status, compact = false }: PipelineProgressProps) {
  const stages = getStageStatus(status);

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        {stages.map((stage, idx) => (
          <div
            key={stage.name}
            className={cn(
              'w-4 h-4 rounded-full flex items-center justify-center',
              stage.icon === 'check' && 'bg-green-500',
              stage.icon === 'progress' && 'bg-blue-500',
              stage.icon === 'pending' && 'bg-gray-300',
              stage.icon === 'blocked' && 'bg-red-500'
            )}
            title={`${stage.name}: ${stage.value}`}
          >
            {stage.icon === 'check' && <CheckCircle2 className="w-3 h-3 text-white" />}
            {stage.icon === 'progress' && <Loader2 className="w-3 h-3 text-white animate-spin" />}
            {stage.icon === 'pending' && <Circle className="w-3 h-3 text-gray-500" />}
            {stage.icon === 'blocked' && <AlertCircle className="w-3 h-3 text-white" />}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2">
      {stages.map((stage, idx) => (
        <div key={stage.name} className="flex flex-col items-center flex-1">
          <div
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center mb-1',
              stage.icon === 'check' && 'bg-green-100 text-green-600',
              stage.icon === 'progress' && 'bg-blue-100 text-blue-600',
              stage.icon === 'pending' && 'bg-gray-100 text-gray-400',
              stage.icon === 'blocked' && 'bg-red-100 text-red-600'
            )}
          >
            {stage.icon === 'check' && <CheckCircle2 className="w-5 h-5" />}
            {stage.icon === 'progress' && <Loader2 className="w-5 h-5 animate-spin" />}
            {stage.icon === 'pending' && <Circle className="w-5 h-5" />}
            {stage.icon === 'blocked' && <AlertCircle className="w-5 h-5" />}
          </div>
          <span className="text-xs font-medium text-gray-700">{stage.name}</span>
          <span className="text-xs text-gray-500">{stage.value}</span>
        </div>
      ))}
    </div>
  );
}
